import clsx from 'clsx';
import EditorialGuide from 'components/Shared/EditorialGuide';
import GetInTouch from 'components/Shared/GetInTouch';
import Breadcrumbs from 'components/ui/Breadcrumbs';
import BlockLoaderNonFields from 'components/ui/ContentBlocks/BlockLoaderNonFields';
import Highlight from 'components/ui/Highlight';
import Image from 'components/ui/Image';
import { useTranslation } from 'hooks/useTranslation';
import styles from './Author.module.scss';
import AuthorInfo from './AuthorInfo';
import StructuredData from './StructuredData';
import type { QunoAuthor } from 'types/Author';
import type { RichTextConfig } from 'components/ui/ContentBlocks/RichText';

type Props = {
  entry: QunoAuthor;
};

const Author = ({ entry }: Props): JSX.Element => {
  const { t } = useTranslation();

  const { name, slug, photo, city, highlight1, highlight2, description } =
    entry;

  const breadcrumbs = [
    {
      link: t('authors').toLowerCase(),
      title: t('authors'),
    },
    {
      link: slug,
      title: name,
    },
  ];

  const richTextConfig: RichTextConfig = {
    'heading-1': clsx(styles.headingShared, styles.customRichH1),
    'heading-2': clsx(styles.headingShared, styles.customRichH2),
    'heading-3': clsx(styles.headingShared, styles.customRichH3),
    'heading-4': clsx(styles.headingShared, styles.customRichH4),
    'heading-5': clsx(styles.headingShared, styles.customRichH5),
    'heading-6': clsx(styles.headingShared, styles.customRichH6),
    hyperlink: styles.hyperlink,
  };

  return (
    <>
      <StructuredData entry={entry} />
      <div className={styles.container}>
        <Breadcrumbs
          className={styles.breadcrumbs}
          containerClassNames={styles.breadcrumbsContainer}
          crumbs={breadcrumbs}
        />
        <div className={styles.author}>
          {photo && (
            <>
              <div className={styles.imageWrapper}>
                <Image
                  className={styles.image}
                  src={photo.url}
                  focus="face"
                  fit="thumb"
                  alt={name}
                  height={320}
                  width={333}
                />
              </div>

              <div className={styles.imageMobileWrapper}>
                <Image
                  className={styles.imageMobile}
                  src={photo.url}
                  focus="face"
                  fit="thumb"
                  alt={name}
                  fill
                />
              </div>
            </>
          )}
          <div className={clsx(styles.infoWrapper, !photo && styles.noPhoto)}>
            <div className={styles.info}>
              <AuthorInfo
                name={name}
                city={city}
                description={description}
                styles={styles}
              />
              {(highlight1 || highlight2) && (
                <ul className={styles.highlights}>
                  {highlight1 && (
                    <Highlight highlight={highlight1} wrapper="li" />
                  )}
                  {highlight2 && (
                    <Highlight highlight={highlight2} wrapper="li" />
                  )}
                </ul>
              )}
            </div>
            {entry.blocks && (
              <div className={styles.blocks}>
                <BlockLoaderNonFields
                  entry={entry}
                  field="blocks"
                  config={richTextConfig}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.guideContainer}>
        <div className={styles.guideWrapper}>
          <div className={styles.guide}>
            <EditorialGuide />
          </div>
        </div>
      </div>
      <GetInTouch />
    </>
  );
};

export default Author;
